import React, { useRef, useEffect, useState } from "react";
import RealTimeRequestPresenter from "./RealTimeRequestPresenter";
import { useQuery } from "@apollo/client";
import {
  INITIALIZE_REALTIME_REQUEST,
} from "../../graphql/monitor";
import {
  useRealTimeDispatch,
  useRealTimeState,
} from "../reducer/RealTimeContext";

import { useRegisterData, useBankingData, useAbuseData } from "../../../context/SubscriptionContext";
import notification from "../../common/notification";
import audioManager from "../../../utils/audioManager";
import { mainClient } from "../../../apollo";
import { ADMIN_CUSTOMER_INQUIRIES_WITH_REPLIES } from "../../graphql/customerInquiry";
import { useSubscriptionEvent } from "../../../utils/subscriptionEvents";
import {
  buildProfitAlertPopupConfig,
  createTimedEventGate,
  getProfitAlertEventKey,
} from "./realtimeAlertUtils.mjs";

/**
 * 팝업 열기 (사운드는 메인 페이지의 AudioManager에서 재생)
 */
const openChildWindow = (type) => {
  var childWindow = window.open(
    `${window.location.origin}/#/popup?type=${type}`,
    "_blank",
    "width=270, height=95, top=300, left=300"
  );
  if (childWindow) {
    childWindow.addEventListener("beforeunload", function (event) {
      console.log("popup 닫기");
    });
  }
};

const RealTimeRequestContainer = () => {
  const { registerUserData, requestBankingData } = useRealTimeState();
  const dispatch = useRealTimeDispatch();
  const serviceWorkerRef = useRef(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const profitAlertGateRef = useRef(null);

  if (!profitAlertGateRef.current) {
    profitAlertGateRef.current = createTimedEventGate({ ttlMs: 3000 });
  }

  // Service Worker 등록 및 Notification 권한 요청
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if ("Notification" in window) {
        const permission = await Notification.requestPermission();
        console.log("Notification 권한:", permission);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          serviceWorkerRef.current = registration;
          console.log("Service Worker 등록 성공");
          requestNotificationPermission();
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);

  // 사용자 상호작용 감지 및 AudioManager 초기화
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (!audioInitialized) {
        const success = await audioManager.initialize();
        if (success) {
          setAudioInitialized(true);
          console.log("🔊 AudioManager 초기화 완료:", audioManager.getStatus());
        }
      }
    };

    // 다양한 사용자 상호작용 이벤트 등록
    const events = ['click', 'touchstart', 'keydown', 'mousedown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [audioInitialized]);

  // 백그라운드 알림 표시
  const showBackgroundNotification = async (options) => {
    if ("Notification" in window && Notification.permission === "granted") {
      if (serviceWorkerRef.current) {
        try {
          await serviceWorkerRef.current.showNotification(options.title, {
            body: options.body,
            tag: options.tag,
            requireInteraction: true,
            data: options.data,
          });
        } catch (error) {
          console.error("Failed to show notification:", error);
        }
      }
    }
  };

  // 새로운 회원가입 처리
  const handleNewRegister = (data) => {
    const safeData = data ?? [];
    const currentLength = registerUserData?.length ?? 0;
    const newLength = safeData.length;

    // 새로운 요청이 있을 때만 알림
    if (newLength > 0 && currentLength < newLength) {
      // 🔊 AudioManager로 사운드 재생 (메인 페이지에서)
      audioManager.play("newMember");

      // 팝업 열기
      openChildWindow("register");

      // 백그라운드 알림
      showBackgroundNotification({
        title: "HTS Manager",
        body: "새로운 회원가입 요청이 있습니다",
        tag: "register-request",
        data: { type: "register" },
      });
    }
    dispatch({ type: "REQUEST_REGISTER", data: safeData });
  };

  // 새로운 입출금 처리
  const handleNewBanking = (data) => {
    const safeData = data ?? [];
    const currentLength = requestBankingData?.length ?? 0;
    const newLength = safeData.length;

    // 새로운 요청이 있을 때만 알림
    if (newLength > 0 && currentLength < newLength) {
      const lastRequest = safeData[newLength - 1];
      const windowType =
        lastRequest.isDeposit === null ? "bankingaAccount" : "banking";

      // 🔊 요청 유형에 따라 적절한 사운드 재생
      let soundKey = "bankOut"; // 기본값: 출금
      if (lastRequest.isDeposit === null) {
        soundKey = "accountNo"; // 계좌번호 요청
      } else if (lastRequest.isDeposit === true) {
        soundKey = "bankIn"; // 입금
      }
      audioManager.play(soundKey);

      // 팝업 열기
      openChildWindow(windowType);

      // 백그라운드 알림
      showBackgroundNotification({
        title: "HTS Manager",
        body:
          lastRequest.isDeposit === null
            ? "계좌승인 요청이 있습니다"
            : "입출금 요청이 있습니다",
        tag: "banking-request",
        data: { type: windowType },
      });
    }
    dispatch({ type: "REQUEST_BANKING", data: safeData });
  };

  // Context에서 구독 데이터 가져오기
  const registerData = useRegisterData();
  const bankingData = useBankingData();
  const abuseData = useAbuseData();

  // 초기 데이터 로드
  useQuery(INITIALIZE_REALTIME_REQUEST, {
    fetchPolicy: "cache-and-network",
    onCompleted: ({ initializeRealTimeRequest }) => {
      dispatch({
        type: "INITIALIZE_REALTIME_REQUEST",
        data: initializeRealTimeRequest,
      });
    },
    onError: (error) => console.log("monitorData initializing error", error),
  });

  // 회원가입 요청 데이터 변경 감지
  useEffect(() => {
    if (registerData !== undefined) {
      handleNewRegister(registerData);
    }
  }, [registerData]);

  // 입출금 요청 데이터 변경 감지
  useEffect(() => {
    if (bankingData !== undefined) {
      handleNewBanking(bankingData);
    }
  }, [bankingData]);

  // 초기 문의 로드 (대기중 OR 처리중+마지막답변이 유저)
  useEffect(() => {
    const fetchActiveInquiries = async () => {
      try {
        const { data } = await mainClient.query({
          query: ADMIN_CUSTOMER_INQUIRIES_WITH_REPLIES,
          fetchPolicy: "no-cache",
          variables: { skip: 0, take: 100 },
        });
        const all = data?.adminInquiries?.data || [];
        const active = all.filter((item) => {
          if (item.status === 0) return true; // PENDING
          if (item.status === 1) {
            // IN_PROGRESS: 마지막 답변이 유저인 경우만
            const replies = item.replies || [];
            if (replies.length === 0) return true;
            return !replies[replies.length - 1].isAdminReply;
          }
          return false;
        });
        dispatch({ type: "REQUEST_INQUIRY", data: active });
      } catch (error) {
        console.error("❌ 문의 로드 실패:", error);
      }
    };
    fetchActiveInquiries();
  }, []);

  useSubscriptionEvent("newInquiry", (inquiry) => {
    dispatch({ type: "ADD_INQUIRY", data: inquiry });
  });

  useSubscriptionEvent("profitAlert", (item) => {
    const eventKey = getProfitAlertEventKey(item);
    if (!profitAlertGateRef.current?.shouldProcess(eventKey)) {
      return;
    }

    const config = buildProfitAlertPopupConfig(item);

    audioManager.play("newAbuse");
    openChildWindow(config.popupType);
  });

  // 이상거래 감지 데이터 변경 감지
  useEffect(() => {
    if (abuseData) {
      const { count, userId, itemCode, isBuy, cycleCount } = abuseData;
      const direction = isBuy ? "매수" : "매도";
      const detail = itemCode
        ? ` [${itemCode}/${direction}] ${cycleCount}회 반복`
        : "";
      notification({
        title: "HTS MANAGER",
        content: `${userId}의 ${count}번째 이상주문 감지${detail}`,
      });

      showBackgroundNotification({
        title: "HTS Manager - 이상주문 감지",
        body: `${userId}의 ${count}번째 이상주문 감지${detail}`,
        tag: "abuse-detection",
        data: { type: "abuse", userId, count },
      });
    }
  }, [abuseData]);

  return (
    <div className="min-w-[240px] h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-center h-14 font-bold text-sm border-b border-gray-200 shrink-0">
        <div>실시간 요청</div>
      </div>
      <div className="p-2 flex-1 min-h-0 flex flex-col overflow-hidden">
        <RealTimeRequestPresenter />
      </div>
    </div>
  );
};

export default RealTimeRequestContainer;
